import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { ConfirmModal } from '../components/ConfirmModal.tsx';
import { Field, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { Asset, AssetDetail } from '../lib/types.ts';
import { QuestionnaireWizard } from './Questionnaire.tsx';

function assetName(v: Asset): string {
  return [v.year, v.make, v.model].filter(Boolean).join(' ') || v.registration_plate || 'Asset';
}

function renewal(date: string | null): string {
  if (!date) return 'No date';
  const days = Math.ceil((new Date(`${date}T00:00:00Z`).getTime() - Date.now()) / 86400000);
  return days < 0 ? `${date} (overdue)` : `${date} (${days}d)`;
}

export function CompanyAssets() {
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'assets'],
    queryFn: () => companyApi.get<{ assets: Asset[] }>('/api/me/assets'),
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  if (isLoading) return <CenteredSpinner />;
  const assets = data?.assets ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Assets</h1>
          <p className="mt-1 text-sm text-slate-500">
            Your assets, their insurance and registration renewals, and audit photos. Renewal
            reminders are sent to you automatically.
          </p>
        </div>
        <Btn onClick={() => setCreating(true)}>+ New asset</Btn>
      </div>

      {assets.length === 0 ? (
        <EmptyState
          title="No assets"
          description="No assets have been added for your company yet."
          action={<Btn onClick={() => setCreating(true)}>+ New asset</Btn>}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {assets.map((v) => (
            <button
              key={v.id}
              onClick={() => setOpenId(v.id)}
              className="rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-slate-300 hover:bg-slate-50"
            >
              <div className="font-semibold text-slate-900">{assetName(v)}</div>
              <div className="mt-0.5 text-xs text-slate-500">
                {v.registration_plate ?? 'no plate'} · {v.asset_type ?? 'asset'}
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && <CreateAssetModal onClose={() => setCreating(false)} />}
      {openId && <AssetModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function CreateAssetModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    make: '',
    model: '',
    year: '',
    registration_plate: '',
    asset_type: '',
  });
  const [error, setError] = useState<unknown>(null);

  const create = useMutation({
    mutationFn: (f: typeof form) =>
      companyApi.post('/api/me/assets', {
        make: f.make.trim() || null,
        model: f.model.trim() || null,
        year: f.year ? Number(f.year) : null,
        registration_plate: f.registration_plate.trim() || null,
        asset_type: f.asset_type.trim() || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'assets'] });
      onClose();
    },
    onError: setError,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="New asset"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn onClick={() => create.mutate(form)} loading={create.isPending}>
            Create
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Make">
            <TextInput value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} />
          </Field>
          <Field label="Model">
            <TextInput value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
          </Field>
          <Field label="Year">
            <TextInput
              type="number"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
            />
          </Field>
          <Field label="Registration plate">
            <TextInput
              value={form.registration_plate}
              onChange={(e) => setForm({ ...form, registration_plate: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Type" hint="e.g. Van, Truck, Car">
          <TextInput
            value={form.asset_type}
            onChange={(e) => setForm({ ...form, asset_type: e.target.value })}
          />
        </Field>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}

function AssetModal({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'asset', id],
    queryFn: () => companyApi.get<{ asset: AssetDetail }>(`/api/me/assets/${id}`),
  });
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: (assetId: string) => companyApi.delete(`/api/me/assets/${assetId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'assets'] });
      onClose();
    },
  });

  const v = data?.asset;
  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={v ? assetName(v) : 'Asset'}
        size="lg"
        footer={
          v ? (
            <>
              <Btn variant="danger" onClick={() => setConfirming(true)}>
                Archive
              </Btn>
              <Btn variant="ghost" onClick={onClose}>
                Close
              </Btn>
            </>
          ) : undefined
        }
      >
        {isLoading || !v ? (
          <CenteredSpinner />
        ) : (
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-2 gap-2 text-slate-700">
              <div><span className="text-slate-400">Plate:</span> {v.registration_plate ?? '—'}</div>
              <div><span className="text-slate-400">Type:</span> {v.asset_type ?? '—'}</div>
              <div><span className="text-slate-400">Colour:</span> {v.colour ?? '—'}</div>
              <div><span className="text-slate-400">VIN:</span> {v.vin ?? '—'}</div>
            </div>

            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Insurance</h3>
              {v.asset_insurances.length === 0 ? (
                <p className="italic text-slate-400">None recorded.</p>
              ) : (
                v.asset_insurances.map((i) => (
                  <div key={i.id} className="text-slate-700">
                    {i.insurer} — renewal {renewal(i.renewal_date)}
                  </div>
                ))
              )}
            </div>

            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Registration</h3>
              {v.asset_registrations.length === 0 ? (
                <p className="italic text-slate-400">None recorded.</p>
              ) : (
                v.asset_registrations.map((r) => (
                  <div key={r.id} className="text-slate-700">
                    {r.registration_number ?? 'Registration'} — renewal {renewal(r.renewal_date)}
                  </div>
                ))
              )}
            </div>

            {v.asset_photos.length > 0 && (
              <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Photos</h3>
                <div className="grid grid-cols-3 gap-2">
                  {v.asset_photos.map((p) =>
                    p.url ? (
                      <img key={p.id} src={p.url} alt={p.caption ?? ''} className="h-24 w-full rounded object-cover" />
                    ) : null,
                  )}
                </div>
              </div>
            )}

            <div className="border-t border-slate-200 pt-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Questionnaire</h3>
              <QuestionnaireWizard
                apiPrefix={`/api/me/assets/${id}`}
                meQueryKey={['me', 'asset', id]}
                submitButtonLabel="Submit asset for review"
              />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={confirming}
        title="Archive asset"
        confirmLabel="Archive"
        danger
        loading={remove.isPending}
        elevated
        onConfirm={() => remove.mutate(id)}
        onClose={() => setConfirming(false)}
      >
        <div>
          Archive this asset? It will be removed from your list. Your administrator keeps its history.
          {remove.error != null && (
            <div className="mt-3">
              <ErrorMessage error={remove.error} />
            </div>
          )}
        </div>
      </ConfirmModal>
    </>
  );
}
